import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
} from '@mui/material';
import {
  Bars2Icon,
  TrashIcon,
  ChevronDownIcon,
  XMarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import EditIcon from '../../../Theme/icons/editIcon';
import useChatStore from '../../Chat/store';
import PinCirleIcon from '../../../Theme/icons/pinCircle';
import useThemeStore from 'Theme/store';
import WarningDialog from './warningDialog';

const inputStyles = (theme) => ({
  fontSize: '14px',
  maxWidth: '100%',
  fontFamily: 'Figtree',
  fontWeight: 700,
  padding: 0,
  margin: 0,
  color: theme === 'dark' ? '#EFEFEF' : '#211E21',
  textAlign: 'center',
  border: theme === 'dark' ? '#9091A461' : '#9091A461',
  backgroundColor: theme === 'dark' ? '#39383A' : '#FBFBFA',
});

const LLMExistingCombos = React.memo(
  ({
    id,
    title,
    store,
    position,
    dragHandleProps,
    allModels,
    openModelsDialog,
    startEditing = false,
    onClone,
  }) => {
    // Estados locales
    const [isEditing, setIsEditing] = useState(false);
    const [tempTitle, setTempTitle] = useState(title);
    const [validModels, setValidModels] = useState([]);
    const [showModelsDialog, setShowModelsDialog] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isCheckHovered, setIsCheckHovered] = useState(false);
    const [warningDialogOpen, setWarningDialogOpen] = useState(false);
    const [warningAction, setWarningAction] = useState(null);
    const [isCloning, setIsCloning] = useState(false);

    const { theme } = useThemeStore();

    // Obtención de funciones y estado desde el store
    const {
      selectedCombo,
      createCombo,
      updateSelectedModels,
      isDefaultCombo,
      isStraicoCombo,
      deleteCombo,
      setIsEditingCombo,
      setSelectedCombo,
      combos,
    } = useChatStore((state) => state);

    const isSmallScreen = useMediaQuery('(max-width:600px)');

    useEffect(() => {
      if (startEditing) {
        setIsEditing(true);
      }
    }, [startEditing]);
    // Función de ordenamiento memorizada
    const sortByAttribute = useCallback((arr, attribute) => {
      return arr?.sort((a, b) => {
        if (a[attribute] < b[attribute]) return -1;
        if (a[attribute] > b[attribute]) return 1;
        return 0;
      });
    }, []);

    const openWarningDialog = useCallback((actionType) => {
      setWarningAction(actionType);
      setWarningDialogOpen(true);
    }, []);

    // Handlers para el diálogo de eliminación
    const openDeleteDialog = useCallback(() => {
      setIsDeleteDialogOpen(true);
    }, []);

    const closeDeleteDialog = useCallback(() => {
      setIsDeleteDialogOpen(false);
    }, []);

    const confirmDelete = useCallback(async () => {
      // Close the dialog first to give immediate UI feedback
      closeDeleteDialog();

      try {
        await deleteCombo(id);

        // Show success message
        toast.success('Combo deleted successfully');

        // Update UI with the default combo
        const currentCombo = combos.find((combo) => combo.position === 1);
        if (currentCombo) {
          setSelectedCombo(currentCombo);

          const selectedModels = currentCombo?.llms
            .map((llmId) => allModels.find((model) => model._id === llmId))
            .filter(Boolean)
            .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));

          updateSelectedModels(() => selectedModels);
          localStorage.setItem('currentModels', JSON.stringify(selectedModels));
        }
      } catch (error) {
        console.error('Error deleting combo:', error);
        toast.error('Failed to delete the combo. Please try again.');
      }
    }, [
      id,
      deleteCombo,
      combos,
      updateSelectedModels,
      closeDeleteDialog,
      allModels,
      setSelectedCombo,
    ]);

    // Función para actualizar los modelos seleccionados y almacenarlos
    const setSelectedModels = useCallback(
      (models) => {
        updateSelectedModels(() => sortByAttribute(models, 'abbreviation'));
        localStorage.setItem('currentModels', JSON.stringify(models));
      },
      [sortByAttribute, updateSelectedModels]
    );

    // Handler para entrar en modo edición
    const handleEdit = useCallback(() => {
      if (isStraicoCombo(id)) {
        openWarningDialog('edit');
        return;
      }

      // Si hay un combo seleccionado, usar ese. De lo contrario, usar el id del componente
      const currentId = selectedCombo?._id || id;

      setIsEditingCombo(true);
      setSelectedModels(validModels);
      setSelectedCombo({
        _id: currentId,
        name: tempTitle,
        llms: validModels.map((model) => model._id),
        isDefault: isDefaultCombo(currentId),
      });
      setIsEditing(true);
    }, [
      id,
      tempTitle,
      selectedCombo,
      validModels,
      isDefaultCombo,
      setSelectedCombo,
      setIsEditingCombo,
      setSelectedModels,
      isStraicoCombo,
      openWarningDialog,
    ]);

    // Handler para actualizar el título mientras se edita
    const handleTitleChange = useCallback((event) => {
      setTempTitle(event.target.value);
    }, []);

    // Handler para guardar los cambios con las validaciones necesarias
    const handleSaveEdit = useCallback(async () => {
      const trimmedTitle = tempTitle.trim();

      // Validaciones:
      if (!trimmedTitle) {
        setErrorMessage('The combo name cannot be empty.');
        toast.error('The combo name cannot be empty.');
        return;
      }

      if (trimmedTitle.toLowerCase() === 'custom combination') {
        setErrorMessage('The name "Custom Combination" is not allowed.');
        toast.error('The name "Custom Combination" is not allowed.');
        return;
      }

      const maxComboNameLength = 15;
      if (trimmedTitle.length > maxComboNameLength) {
        const errMsg = `The name cannot exceed ${maxComboNameLength} characters.`;
        setErrorMessage(errMsg);
        toast.error(errMsg);
        return;
      }

      // Asegurarse de que estamos comprobando el id correcto (el del combo actualmente seleccionado)
      const currentId = selectedCombo?._id || id;

      const isDuplicateTitle = combos.some(
        (combo) =>
          combo.name.toLowerCase() === trimmedTitle.toLowerCase() &&
          combo._id !== currentId
      );
      if (isDuplicateTitle) {
        setErrorMessage('A combo with this name already exists.');
        toast.error('A combo with this name already exists.');
        return;
      }

      const selectedModelIds = validModels.map((model) => model._id).sort();
      const isDuplicateModels = combos.some((combo) => {
        const comboModelIds = (combo.llms || []).sort();
        return (
          combo._id !== currentId &&
          comboModelIds.length === selectedModelIds.length &&
          comboModelIds.every((mId, idx) => mId === selectedModelIds[idx])
        );
      });
      if (isDuplicateModels) {
        setErrorMessage('A combo with the same models already exists.');
        toast.error('A combo with the same models already exists.');
        return;
      }

      if (validModels.length === 0) {
        setErrorMessage('You must select at least one model.');
        toast.error('You must select at least one model.');
        return;
      }

      // Sin errores, se intenta la actualización
      setErrorMessage('');
      try {
        // Usar el ID del combo seleccionado o el ID original del componente
        await store.api.put(`/combos/llm-combos/${currentId}`, {
          name: trimmedTitle,
          llms: validModels.map((model) => model._id),
        });
        toast.success('Combo updated successfully!');

        // Actualizar el combo seleccionado con el ID correcto
        setSelectedCombo({
          _id: currentId,
          name: trimmedTitle,
          llms: validModels.map((model) => model._id),
          isDefault: isDefaultCombo(currentId),
        });

        // Actualizar el estado global
        useChatStore.setState((state) => ({
          combos: state.combos.map((combo) =>
            combo._id === currentId
              ? {
                  ...combo,
                  name: trimmedTitle,
                  llms: validModels.map((model) => model._id),
                }
              : combo
          ),
        }));
      } catch (error) {
        console.error('Error updating combo:', error);
        toast.error('Failed to update combo.');
      } finally {
        setIsEditing(false);
        setIsEditingCombo(false);
      }
    }, [
      tempTitle,
      combos,
      id,
      selectedCombo,
      validModels,
      store.api,
      isDefaultCombo,
      setSelectedCombo,
      setIsEditingCombo,
    ]);

    // Referencia para detectar clics fuera del input de edición
    const editBoxRef = useRef(null);
    useEffect(() => {
      if (!isEditing) return;
      const handleClickOutside = (event) => {
        if (event.target.closest('.save-button')) {
          return;
        }
        if (editBoxRef.current && !editBoxRef.current.contains(event.target)) {
          setIsEditing(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isEditing]);

    // Handler para teclas en el input (Enter y Escape)
    const handleKeyDown = useCallback(
      (event) => {
        if (event.key === 'Enter') {
          handleSaveEdit();
        } else if (event.key === 'Escape') {
          setTempTitle(title);
          setIsEditing(false);
        }
      },
      [handleSaveEdit, title]
    );

    const transformedCombos = useMemo(() => {
      return (combos || []).map((combo) => {
        const modelsForCombo = (combo.llms || [])
          .map((comboId) =>
            allModels.find((model) => String(model._id) === String(comboId))
          )
          .filter(Boolean)
          .map((model) => ({
            ...model,
            provider: model.platform || 'Unknown',
            model: model.abbreviation || model.name || 'Unknown Model',
          }));

        return {
          id: combo._id,
          title: combo.name || 'Untitled Combo',
          models: modelsForCombo,
          isDefault: combo.isDefault,
        };
      });
    }, [combos, allModels]);

    const currentCombo = transformedCombos.find((c) => c.id === id);

    useEffect(() => {
      setValidModels(currentCombo ? currentCombo.models : []);
    }, [currentCombo]);

    const handleSelectModels = useCallback(
      async (models, overrideId = null) => {
        // Add overrideId parameter
        try {
          const updatedModels = models.filter((item) => item && item._id);

          // Use overrideId if provided, otherwise use the component's id
          const comboId = overrideId || id;

          // Find the current combo to preserve its position
          const currentCombo = useChatStore
            .getState()
            .combos.find((c) => c._id === comboId);

          if (!currentCombo) {
            throw new Error('Combo not found');
          }

          // Update the backend using the correct combo id
          await store.api.put(`/combos/llm-combos/${comboId}`, {
            llms: updatedModels.map((item) => item._id),
            position: currentCombo.position,
          });

          // Update the selected combo if it matches the current one
          if (selectedCombo && selectedCombo._id === comboId) {
            setSelectedCombo({
              ...selectedCombo,
              llms: updatedModels.map((model) => model._id),
              position: currentCombo.position,
            });
          }

          // Update the global combos list
          useChatStore.setState((state) => ({
            combos: state.combos.map((combo) =>
              combo._id === comboId
                ? {
                    ...combo,
                    llms: updatedModels.map((m) => m._id),
                    position: currentCombo.position,
                  }
                : combo
            ),
          }));

          setValidModels(updatedModels);
          updateSelectedModels(() =>
            sortByAttribute(updatedModels, 'abbreviation')
          );
          localStorage.setItem('currentModels', JSON.stringify(updatedModels));

          toast.success('Models updated successfully!');
        } catch (error) {
          console.error('Error updating models for the combo:', error);
          toast.error('Failed to update models. Please try again.');
        }
      },
      [
        id,
        selectedCombo,
        store.api,
        updateSelectedModels,
        sortByAttribute,
        setSelectedCombo,
      ]
    );

    // Handler para configurar el combo y abrir el diálogo de modelos
    const setComboAndShowDialog = useCallback(() => {
      setIsEditingCombo(true);

      // Importante: Usar siempre el ID del componente actual
      const comboId = id;

      // Asegurarnos de usar modelos válidos y actualizados
      const updatedModels = validModels
        .map((model) => allModels.find((m) => m._id === model._id))
        .filter(Boolean)
        .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));

      // Actualizar el combo seleccionado con el ID correcto
      setSelectedCombo({
        _id: comboId,
        name: tempTitle,
        llms: validModels.map((model) => model._id),
        isDefault: isDefaultCombo(comboId),
      });

      setValidModels(updatedModels);
      updateSelectedModels(() =>
        sortByAttribute(updatedModels, 'abbreviation')
      );
      localStorage.setItem('currentModels', JSON.stringify(updatedModels));

      // Pasar el handleSelectModels directamente sin wrapper adicional
      openModelsDialog({
        selectedModels: updatedModels,
        handleSelectModels,
      });
    }, [
      id,
      tempTitle,
      validModels,
      allModels,
      isDefaultCombo,
      setSelectedCombo,
      updateSelectedModels,
      sortByAttribute,
      openModelsDialog,
      handleSelectModels,
    ]);

    const backgroundColor =
      position !== 1 ? 'transparent' : theme === 'dark' ? '#1D1C1E' : '#EDEDF2';

    const memoizedInputStyles = useMemo(() => inputStyles(theme), [theme]);

    const handleCloseModelsDialog = useCallback(() => {
      setShowModelsDialog(false);
    }, []);

    // Functions to close and confirm the warning dialog
    const handleWarningDialogClose = useCallback(() => {
      setWarningDialogOpen(false);
      setWarningAction(null);
    }, []);

    const handleWarningDialogConfirm = useCallback(async () => {
      if (warningAction === 'edit' && !isCloning) {
        try {
          // Prevenir múltiples clonaciones simultáneas
          setIsCloning(true);

          // Generar un nombre único para la copia
          let baseName = tempTitle;

          let newName = `${baseName}`;
          let counter = 1;

          // Verificar si ya existe un combo con ese nombre
          while (combos.some((combo) => combo.name === newName)) {
            counter++;
            newName = `${baseName} copy ${counter}`;
          }

          // 1. Create the clone
          const cloned = await onClone({
            baseName: newName,
            validModels,
            combos,
          });

          // 2. If cloning failed, stop here
          if (!cloned) {
            setIsCloning(false);
            return;
          }

          // 3. Update local state with cloned combo info
          setTempTitle(cloned.name);

          // 4. Sort the models
          const sortedModels = [...validModels].sort((a, b) =>
            a.abbreviation.localeCompare(b.abbreviation)
          );

          // 5. Update global state with the cloned combo
          setSelectedCombo(cloned);
          updateSelectedModels(() => sortedModels);
          localStorage.setItem('currentModels', JSON.stringify(sortedModels));

          // 6. Close warning dialog
          setWarningDialogOpen(false);
          setWarningAction(null);

          // 7. Keep edit mode active for the cloned combo
          setIsEditing(true);
          setIsEditingCombo(true);

          // 8. Open models dialog for the cloned combo, passing the cloned ID
          openModelsDialog({
            selectedModels: sortedModels,
            handleSelectModels: (models) =>
              handleSelectModels(models, cloned._id),
          });

          toast.success('Combo cloned successfully!');
        } catch (error) {
          console.error('Error during clone and edit:', error);
          toast.error('Failed to clone and edit combo');
        } finally {
          // Asegurarse de que isCloning se restablezca incluso si hay un error
          setIsCloning(false);
        }
      }
    }, [
      warningAction,
      isCloning,
      onClone,
      tempTitle,
      validModels,
      combos,
      setSelectedCombo,
      updateSelectedModels,
      openModelsDialog,
      handleSelectModels,
    ]);

    // Asegurarse de que la condición de eliminación es correcta
    const canDelete = !isDefaultCombo(id) && !isStraicoCombo(id);

    const handleModelsClick = useCallback(() => {
      if (isStraicoCombo(id)) {
        openWarningDialog('edit');
      } else if (!isDefaultCombo(id)) {
        setComboAndShowDialog();
      }
    }, [
      id,
      isStraicoCombo,
      isDefaultCombo,
      openWarningDialog,
      setComboAndShowDialog,
    ]);

    return (
      <>
        {isSmallScreen ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '100%',
            }}
          >
            <Box
              sx={{
                marginLeft: '28%',
                display: 'flex',
                alignItems: 'center',
                width: '50%',
              }}
            >
              <Box sx={{ width: '22px', height: '22px' }}>
                {isEditing && !isDefaultCombo(id) && !isStraicoCombo(id) ? (
                  <Box
                    className="save-button"
                    onMouseEnter={() => setIsCheckHovered(true)}
                    onMouseLeave={() => setIsCheckHovered(false)}
                    onClick={() => handleSaveEdit()}
                    sx={{ cursor: 'pointer' }}
                  >
                    {isCheckHovered ? (
                      <CheckCircleIconSolid
                        style={{
                          width: '24px',
                          height: '24px',
                          color: '#5256A6',
                        }}
                      />
                    ) : (
                      <CheckCircleIcon
                        style={{
                          width: '24px',
                          height: '24px',
                          color: '#9091A4',
                        }}
                      />
                    )}
                  </Box>
                ) : (
                  <EditIcon
                    style={{
                      width: '22px',
                      height: '22px',
                      color: theme === 'dark' ? '#858585' : '#9091A4',
                      cursor: !isDefaultCombo(id) ? 'pointer' : 'default',
                      opacity: isDefaultCombo(id) ? 0 : 1,
                      pointerEvents: isDefaultCombo(id) ? 'none' : 'auto',
                    }}
                    onClick={!isDefaultCombo(id) ? handleEdit : null}
                  />
                )}
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                ref={editBoxRef}
              >
                {isEditing && !isDefaultCombo(id) && !isStraicoCombo(id) ? (
                  <input
                    autoFocus
                    value={tempTitle}
                    onChange={handleTitleChange}
                    onKeyDown={handleKeyDown}
                    style={memoizedInputStyles}
                  />
                ) : (
                  <Typography
                    onClick={isDefaultCombo(id) ? null : handleEdit}
                    sx={{
                      fontSize: '14px',
                      maxWidth: '100%',
                      fontFamily: 'Figtree',
                      fontWeight: 700,
                      color: theme === 'dark' ? '#FFFFFF' : '#211E21',
                      textAlign: 'center',
                      lineHeight: 1.5,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {tempTitle}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: '10px',
                backgroundColor,
                padding: '8px 0',
                borderRadius: '16px',
              }}
              className="editable-input"
            >
              <Box {...dragHandleProps} data-drag-handle>
                <Bars2Icon
                  style={{
                    marginLeft: '8px',
                    width: '22px',
                    cursor: 'grab',
                    height: '22px',
                    color: theme === 'dark' ? '#858585' : '#9091A4',
                    touchAction: 'none',
                  }}
                />
              </Box>

              <Box
                onClick={isDefaultCombo(id) ? null : handleModelsClick}
                sx={{
                  flexGrow: 1,
                  maxWidth: { xs: '100%', sm: '75%' },
                  minHeight: '51px',
                  backgroundColor: theme === 'dark' ? '#39383A' : '#FBFBFA',
                  borderRadius: '16px',
                  border: `1.5px solid ${
                    theme === 'dark' ? '#9091A461' : '#9091A461'
                  }`,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  padding: { xs: '8px', sm: '8px 12px' },
                  paddingRight: '36px',
                  gap: '4px',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: isDefaultCombo(id) ? 'default' : 'pointer',
                  opacity: isDefaultCombo(id) ? 0.5 : 1,
                }}
              >
                {validModels.length > 0 ? (
                  validModels
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((model, index) => (
                      <Box
                        key={index}
                        sx={{
                          backgroundColor:
                            theme === 'dark' ? '#787878' : '#EDEDF2',
                          borderRadius: '24px',
                          padding: '2px 12px',
                          marginRight: '1%',
                          maxWidth: '80%',
                          fontSize: '12px',
                          color: theme === 'dark' ? '#EFEFEF' : '#51526C',
                          fontFamily: 'Figtree',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          '@media (max-width:500px)': {
                            maxWidth: '80%',
                          },
                        }}
                      >
                        {model.name}
                      </Box>
                    ))
                ) : (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: theme === 'dark' ? '#FFFFFF' : '#9091A4',
                    }}
                  >
                    Loading models....
                  </Typography>
                )}
                <ChevronDownIcon
                  style={{
                    width: '18px',
                    height: '18px',
                    color: theme === 'dark' ? '#FFFFFF' : '#9091A4',
                    position: 'absolute',
                    top: '50%',
                    right: '12px',
                    transform: 'translateY(-50%)',
                    fontWeight: 700,
                  }}
                />
              </Box>

              <Box sx={{ width: '24px', height: '24px' }}>
                <TrashIcon
                  style={{
                    width: '22px',
                    height: '22px',
                    color: theme === 'dark' ? '#FFFFFF' : '#9091A4',
                    cursor: canDelete ? 'pointer' : 'default',
                    opacity: canDelete ? 1 : 0,
                    pointerEvents: canDelete ? 'auto' : 'none',
                  }}
                  onClick={
                    canDelete ? () => setIsDeleteDialogOpen(true) : undefined
                  }
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            {position === 1 && window.innerWidth > 600 && (
              <PinCirleIcon
                circleFill={theme === 'dark' ? '#7F82C3' : '#5256A6'}
                innerFill={theme === 'dark' ? '#252425' : '#F7F7F8'}
                stroke="none"
                style={{
                  width: '30px',
                  height: '30px',
                  marginLeft: '-60px',
                  marginRight: '30px',
                }}
              />
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: '10px',
                backgroundColor,
                padding: '8px 0',
                borderRadius: '16px',
              }}
              className="editable-input"
            >
              <Box {...dragHandleProps} data-drag-handle>
                <Bars2Icon
                  style={{
                    marginLeft: '8px',
                    width: '22px',
                    cursor: 'grab',
                    height: '22px',
                    color: theme === 'dark' ? '#858585' : '#9091A4',
                    touchAction: 'none',
                  }}
                />
              </Box>

              <Box sx={{ width: '22px', height: '22px' }}>
                {isEditing && !isDefaultCombo(id) ? (
                  <Box
                    className="save-button"
                    onMouseEnter={() => setIsCheckHovered(true)}
                    onMouseLeave={() => setIsCheckHovered(false)}
                    onClick={() => handleSaveEdit()}
                    sx={{ cursor: 'pointer' }}
                  >
                    {isCheckHovered ? (
                      <CheckCircleIconSolid
                        style={{
                          width: '24px',
                          height: '24px',
                          color: '#5256A6',
                        }}
                      />
                    ) : (
                      <CheckCircleIcon
                        style={{
                          width: '24px',
                          height: '24px',
                          color: '#9091A4',
                        }}
                      />
                    )}
                  </Box>
                ) : (
                  <EditIcon
                    style={{
                      width: '22px',
                      height: '22px',
                      color: theme === 'dark' ? '#858585' : '#9091A4',
                      cursor: !isDefaultCombo(id) ? 'pointer' : 'default',
                      opacity: isDefaultCombo(id) ? 0 : 1,
                      pointerEvents: isDefaultCombo(id) ? 'none' : 'auto',
                    }}
                    onClick={!isDefaultCombo(id) ? handleEdit : undefined}
                  />
                )}
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  maxWidth: '10%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                ref={editBoxRef}
              >
                {isEditing && !isDefaultCombo(id) ? (
                  <input
                    autoFocus
                    value={tempTitle}
                    onChange={handleTitleChange}
                    onKeyDown={handleKeyDown}
                    style={memoizedInputStyles}
                  />
                ) : (
                  <Typography
                    onClick={isDefaultCombo(id) ? null : handleEdit}
                    sx={{
                      fontSize: '14px',
                      maxWidth: '100%',
                      fontFamily: 'Figtree',
                      fontWeight: 700,
                      color: theme === 'dark' ? '#FFFFFF' : '#211E21',
                      textAlign: 'center',
                      lineHeight: 1.5,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {tempTitle}
                  </Typography>
                )}
              </Box>

              <Box
                onClick={isDefaultCombo(id) ? null : handleModelsClick}
                sx={{
                  flexGrow: 1,
                  maxWidth: { xs: '50%', sm: '75%' },
                  minHeight: '51px',
                  backgroundColor: theme === 'dark' ? '#39383A' : '#FBFBFA',
                  borderRadius: '16px',
                  border: `1.5px solid ${
                    theme === 'dark' ? '#9091A461' : '#9091A461'
                  }`,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  padding: { xs: '8px', sm: '8px 12px' },
                  paddingRight: '36px',
                  gap: '4px',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: isDefaultCombo(id) ? 'default' : 'pointer',
                  opacity: isDefaultCombo(id) ? 0.5 : 1,
                }}
              >
                {validModels.length > 0 ? (
                  validModels
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((model, index) => (
                      <Box
                        key={index}
                        sx={{
                          backgroundColor:
                            theme === 'dark' ? '#787878' : '#EDEDF2',
                          borderRadius: '24px',
                          padding: '2px 12px',
                          marginRight: '1%',
                          maxWidth: '80%',
                          fontSize: '12px',
                          color: theme === 'dark' ? '#EFEFEF' : '#51526C',
                          fontFamily: 'Figtree',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          '@media (max-width:500px)': {
                            maxWidth: '80%',
                          },
                        }}
                      >
                        {model.name}
                      </Box>
                    ))
                ) : (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: theme === 'dark' ? '#FFFFFF' : '#9091A4',
                    }}
                  >
                    Loading models....
                  </Typography>
                )}
                <ChevronDownIcon
                  style={{
                    width: '18px',
                    height: '18px',
                    color: theme === 'dark' ? '#FFFFFF' : '#9091A4',
                    position: 'absolute',
                    top: '50%',
                    right: '12px',
                    transform: 'translateY(-50%)',
                    fontWeight: 700,
                  }}
                />
              </Box>

              <Box sx={{ width: '24px', height: '24px' }}>
                <TrashIcon
                  style={{
                    width: '22px',
                    height: '22px',
                    color: theme === 'dark' ? '#FFFFFF' : '#9091A4',
                    cursor: canDelete ? 'pointer' : 'default',
                    opacity: canDelete ? 1 : 0,
                    pointerEvents: canDelete ? 'auto' : 'none',
                  }}
                  onClick={
                    canDelete ? () => setIsDeleteDialogOpen(true) : undefined
                  }
                />
              </Box>
            </Box>
          </>
        )}

        <Dialog
          open={isDeleteDialogOpen}
          onClose={closeDeleteDialog}
          PaperProps={{
            style: {
              borderRadius: '21px',
              backgroundColor: theme === 'dark' ? '#252425' : '#F7F7F8',
              width: '90%',
              maxWidth: '400px',
              height: 'auto',
              maxHeight: '90vh',
              padding: '16px',
              overflowY: 'auto',
            },
            elevation: 0,
          }}
        >
          <DialogTitle
            sx={{
              textAlign: 'center',
              color: theme === 'dark' ? '#EFEFEF' : '#211E21',
              fontWeight: 700,
              fontSize: '24px',
              fontFamily: 'Figtree',
              lineHeight: '28px',
              '@media (max-width:600px)': {
                fontSize: '20px',
                lineHeight: '24px',
              },
            }}
          >
            Delete combo
          </DialogTitle>
          <DialogContent
            sx={{
              paddingBottom: '1px',
              fontFamily: 'Figtree',
              textAlign: 'center',
              '@media (max-width:600px)': {
                padding: '12px',
              },
            }}
          >
            Are you sure you want to delete this combo?
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: 'center',
              marginTop: '8px',
              fontFamily: 'Figtree',
              gap: '22%',
              '@media (max-width:600px)': {
                marginTop: '8px',
                gap: '16px',
                flexWrap: 'wrap',
              },
            }}
          >
            <Button
              onClick={closeDeleteDialog}
              sx={{
                flex: 1,
                backgroundColor: theme === 'dark' ? '#3B3B45' : '#D4D5E6',
                color: theme === 'dark' ? '#7F82C3' : '#5256A6',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                '&:hover': {
                  backgroundColor: theme === 'dark' ? '#34343E' : '#C7C9DE',
                },
                '@media (max-width:600px)': {
                  fontSize: '14px',
                  padding: '6px 12px',
                },
              }}
            >
              <XMarkIcon className="w-[16px] h-[16px]" />
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              sx={{
                flex: 1,
                backgroundColor: '#464BBA',
                color: '#FFFFFF',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                '&:hover': {
                  backgroundColor: '#3B40A0',
                },
                '@media (max-width:600px)': {
                  fontSize: '14px',
                  padding: '6px 12px',
                },
              }}
            >
              <TrashIcon className="w-[16px] h-[16px]" />
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <WarningDialog
          open={warningDialogOpen}
          onClose={handleWarningDialogClose}
          onConfirm={handleWarningDialogConfirm}
          actionType={warningAction}
          themeValue={theme}
        />
      </>
    );
  }
);

export default LLMExistingCombos;
